import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  Pencil,
  RefreshCw,
  Trash2,
  WandSparkles,
} from 'lucide-react';
import type { AttributeDef, TaskStateProfile, TaskUI } from '@flexiplanner/types';
import { deriveTaskStateMode, numberToPriority, taskToUI } from '@flexiplanner/types';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TaskEditor } from '@/components/blocks/TaskEditor';
import { EnumAttributeInput } from '@/components/attributes/EnumAttributeInput';
import { useToast } from '@/hooks/use-toast';
import { useTasksStore } from '@/stores/tasksStore';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import {
  formatMissingPlanningFields,
  getMissingPlanningFields,
  getPlanningAttributeValue,
} from '@/lib/activityReadiness';
import { cn } from '@/lib/utils';

export function UnscheduledPage() {
  const { toast } = useToast();
  const { tasks, setTasks, updateTask, deleteTask } = useTasksStore();
  const { bundles, loadWorkspace, getBundle } = useWorkspaceStore();
  const [editingTask, setEditingTask] = useState<TaskUI | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [inferring, setInferring] = useState(false);
  const [inferenceStatus, setInferenceStatus] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [availableAttributes, setAvailableAttributes] = useState<AttributeDef[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Partial<TaskUI>>>({});
  const [autoSaveStatus, setAutoSaveStatus] = useState<Record<string, 'saving' | 'saved' | 'error'>>({});
  const [visibleLimit, setVisibleLimit] = useState(80);
  const draftsRef = useRef<Record<string, Partial<TaskUI>>>({});
  const autoSaveTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const draftVersionsRef = useRef<Record<string, number>>({});
  const availableAttributesRef = useRef<AttributeDef[]>([]);
  const autoInferenceKeyRef = useRef('');

  useEffect(() => {
    void refresh();
    void loadWorkspace();
    void loadAttributes();
  }, [loadWorkspace]);

  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    const refreshOnFocus = () => void refresh();
    document.addEventListener('visibilitychange', refreshWhenVisible);
    window.addEventListener('focus', refreshOnFocus);
    return () => {
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      window.removeEventListener('focus', refreshOnFocus);
    };
  }, []);

  useEffect(() => {
    return () => {
      Object.values(autoSaveTimersRef.current).forEach(clearTimeout);
      Object.entries(draftsRef.current).forEach(([taskId, draft]) => {
        const baseTask = useTasksStore.getState().tasks.find(task => task.id === taskId);
        if (!baseTask) return;
        void persistInlineTask(mergeTaskDraft(baseTask, draft), {
          silent: true,
          version: draftVersionsRef.current[taskId],
        });
      });
    };
  }, []);

  const unscheduled = useMemo(
    () => {
      const candidates = tasks
        .filter(task => isVisibleUnscheduledActivity(task))
        .map(task => mergeTaskDraft(task, drafts[task.id]));
      return dedupeImportedActivities(candidates)
        .filter(task => isVisibleUnscheduledActivity(task))
        .sort((a, b) => {
          const aDue = a.deadline ? new Date(a.deadline).getTime() : Infinity;
          const bDue = b.deadline ? new Date(b.deadline).getTime() : Infinity;
          return aDue - bDue || a.title.localeCompare(b.title);
        });
    },
    [tasks, drafts]
  );

  const visibleUnscheduled = useMemo(() => unscheduled.slice(0, visibleLimit), [unscheduled, visibleLimit]);
  const needsDetails = useMemo(
    () => unscheduled.filter(task => getMissingPlanningFields(task, availableAttributes).length > 0),
    [unscheduled, availableAttributes]
  );
  const activeBundles = useMemo(() => bundles.filter(bundle => bundle.status === 'active'), [bundles]);

  useEffect(() => {
    if (refreshing || inferring || visibleUnscheduled.length === 0) return;
    const candidates = visibleUnscheduled.filter(shouldInferActivityPlanning).slice(0, 12);
    if (candidates.length === 0) return;
    const key = candidates.map(task => `${task.id}:${task.updatedAt}`).join('|');
    if (!key || autoInferenceKeyRef.current === key) return;
    autoInferenceKeyRef.current = key;
    const timer = setTimeout(() => {
      void inferPlanningForTasks(candidates.map(task => task.id), { silent: true });
    }, 900);
    return () => clearTimeout(timer);
  }, [visibleUnscheduled, refreshing, inferring]);

  async function refresh() {
    setRefreshing(true);
    try {
      const response = await api.tasks.list();
      if (response.success && response.data) {
        const tasksList = Array.isArray(response.data) ? response.data : (response.data as any)?.tasks;
        if (tasksList) {
          const nextTasks = tasksList.map(taskToUI);
          setTasks(nextTasks);
          const nextTaskIds = new Set(nextTasks.map((task: TaskUI) => task.id));
          const nextDrafts = Object.fromEntries(
            Object.entries(draftsRef.current).filter(([taskId]) => {
              const task = nextTasks.find((item: TaskUI) => item.id === taskId);
              return nextTaskIds.has(taskId) && task && isUnscheduledActivity(task);
            })
          );
          draftsRef.current = nextDrafts;
          setDrafts(nextDrafts);
        }
      }
    } finally {
      setRefreshing(false);
    }
  }

  async function loadAttributes() {
    const response = await api.user.getAttributes();
    if (response.success && response.data) {
      availableAttributesRef.current = response.data;
      setAvailableAttributes(response.data);
    }
  }

  async function handleSaveTask(updates: Partial<TaskUI>) {
    if (!editingTask) return;
    const response = await api.tasks.update(editingTask.id, {
      title: updates.title,
      description: updates.description,
      ...(updates.bundleId ? { bundle_id: updates.bundleId } : {}),
      due_date: updates.deadline,
      do_date: updates.scheduledStart,
      do_date_end: updates.scheduledEnd,
      duration_minutes: updates.estimatedDuration,
      priority: updates.priority ? numberToPriority(updates.priority) : undefined,
      state_profile: updates.stateProfile,
      custom_attributes: updates.customAttributes,
    });

    if (response.success && response.data) {
      updateTask(editingTask.id, taskToUI((response.data as any).task || response.data));
      toast({ title: 'Activity updated', description: 'This activity can be scheduled once all required fields are complete.' });
    } else {
      toast({
        title: 'Could not save activity',
        description: response.error?.message || 'Try again.',
        variant: 'destructive',
      });
    }
  }

  function updateInlineTask(
    taskId: string,
    updates: Partial<TaskUI>,
    options: { saveDelayMs?: number } = {}
  ) {
    const baseTask = useTasksStore.getState().tasks.find(task => task.id === taskId);
    if (!baseTask) return;

    const nextVersion = (draftVersionsRef.current[taskId] || 0) + 1;
    const nextDraft = mergeTaskDraft(draftsRef.current[taskId] || {}, updates);
    const nextDrafts = {
      ...draftsRef.current,
      [taskId]: nextDraft,
    };
    draftsRef.current = nextDrafts;
    draftVersionsRef.current[taskId] = nextVersion;
    setDrafts(nextDrafts);
    setAutoSaveStatus(current => ({ ...current, [taskId]: 'saving' }));

    scheduleAutoSave(mergeTaskDraft(baseTask, nextDraft), nextVersion, options.saveDelayMs);
  }

  function updateInlineProfile(
    task: TaskUI,
    key: StateScoreKey,
    rawValue: string
  ) {
    const value =
      rawValue === ''
        ? null
        : Math.max(0, Math.min(10, Number(rawValue)));
    const stateProfile = {
      ...(task.stateProfile || {}),
      [key]: value,
    };
    updateInlineTask(task.id, {
      stateProfile: {
        ...stateProfile,
        mode: deriveTaskStateMode(stateProfile),
      },
    });
  }

  function updateInlineAttribute(task: TaskUI, attr: AttributeDef, rawValue: string) {
    const value =
      ['Number', 'float', 'integer'].includes(attr.data_type) && rawValue !== ''
        ? Number(rawValue)
        : rawValue;
    updateInlineTask(task.id, {
      customAttributes: {
        ...(task.customAttributes || {}),
        [attr.attr_id]: value,
      },
    });
  }

  function handleAttributeDefinitionUpdated(updatedAttr: AttributeDef) {
    availableAttributesRef.current = availableAttributesRef.current.map(attr =>
      attr.attr_id === updatedAttr.attr_id ? updatedAttr : attr
    );
    setAvailableAttributes(current =>
      current.map(attr => (attr.attr_id === updatedAttr.attr_id ? updatedAttr : attr))
    );
  }

  function scheduleAutoSave(task: TaskUI, version: number, delayMs = 800) {
    if (autoSaveTimersRef.current[task.id]) clearTimeout(autoSaveTimersRef.current[task.id]);
    autoSaveTimersRef.current[task.id] = setTimeout(() => {
      void persistInlineTask(task, { silent: true, version });
    }, delayMs);
  }

  async function persistInlineTask(
    task: TaskUI,
    options: { silent?: boolean; version?: number } = {}
  ) {
    const missing = getMissingPlanningFields(task, availableAttributesRef.current);
    setAutoSaveStatus(current => ({ ...current, [task.id]: 'saving' }));

    try {
      const customAttributes = {
        ...(task.customAttributes || {}),
        needs_review: missing.length > 0,
        required_fields_complete: missing.length === 0,
        missing_required_fields: missing,
      };
      const response = await api.tasks.update(task.id, {
        title: task.title,
        description: task.description,
        ...(task.bundleId ? { bundle_id: task.bundleId } : {}),
        due_date: task.deadline,
        duration_minutes: task.estimatedDuration,
        priority: numberToPriority(task.priority || 3),
        state_profile: task.stateProfile,
        custom_attributes: customAttributes,
      });
      if (!response.success) throw new Error(response.error?.message || 'Save failed');
      if (options.version === undefined || draftVersionsRef.current[task.id] === options.version) {
        updateTask(task.id, taskToUI((response.data as any).task || response.data));
        const nextDrafts = { ...draftsRef.current };
        delete nextDrafts[task.id];
        draftsRef.current = nextDrafts;
        setDrafts(nextDrafts);
        setAutoSaveStatus(current => ({ ...current, [task.id]: 'saved' }));
      } else {
        return;
      }
      if (!options.silent) {
        toast({
          title: missing.length ? 'Activity saved' : 'Activity ready',
          description: missing.length
            ? 'The remaining missing fields are still marked for review.'
            : 'Solve can now place this activity.',
        });
      }
    } catch (error) {
      setAutoSaveStatus(current => ({ ...current, [task.id]: 'error' }));
      if (!options.silent) {
        toast({
          title: 'Could not save row',
          description: error instanceof Error ? error.message : 'Try again.',
          variant: 'destructive',
        });
      }
      delete autoSaveTimersRef.current[task.id];
    }
  }

  async function inferPlanningForTasks(
    taskIds: string[],
    options: { silent?: boolean } = {}
  ) {
    const ids = Array.from(new Set(taskIds)).filter(Boolean);
    if (ids.length === 0) {
      if (!options.silent) {
        toast({ title: 'Nothing to infer', description: 'Visible activities already have inferred planning fields.' });
      }
      return;
    }

    setInferring(true);
    setInferenceStatus(`Inferring ${ids.length} activit${ids.length === 1 ? 'y' : 'ies'}...`);
    try {
      const response = await api.tasks.inferPlanning({ task_ids: ids, apply: true, limit: ids.length });
      if (!response.success) throw new Error(response.error?.message || 'Inference failed');
      const inferredTasks = Array.isArray((response.data as any)?.tasks)
        ? (response.data as any).tasks.map(taskToUI)
        : [];
      for (const task of inferredTasks) {
        updateTask(task.id, task);
      }
      const applied = Number((response.data as any)?.applied || 0);
      setInferenceStatus(applied > 0 ? `Inferred planning fields for ${applied}.` : 'No blank planning fields needed inference.');
      if (!options.silent) {
        toast({
          title: applied > 0 ? 'Planning inferred' : 'Inference complete',
          description:
            applied > 0
              ? 'Blank duration and P/RJ fields were filled without overwriting your entries.'
              : 'There were no blank inferred fields to apply.',
        });
      }
    } catch (error) {
      setInferenceStatus('Inference failed.');
      if (!options.silent) {
        toast({
          title: 'Could not infer planning fields',
          description: error instanceof Error ? error.message : 'Try again.',
          variant: 'destructive',
        });
      }
    } finally {
      setInferring(false);
    }
  }

  async function handleDeleteTask(task: TaskUI) {
    if (!window.confirm(`Delete "${task.title}" from unscheduled activities?`)) return;
    setDeletingId(task.id);
    try {
      const response = await api.tasks.delete(task.id);
      if (!response.success) throw new Error(response.error?.message || 'Delete failed');
      deleteTask(task.id);
      toast({ title: 'Activity deleted', description: 'It was removed from the unscheduled list.' });
    } catch (error) {
      toast({
        title: 'Could not delete activity',
        description: error instanceof Error ? error.message : 'Try again.',
        variant: 'destructive',
      });
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-5">
      <header className="flex flex-col gap-3 border-b pb-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <CalendarClock className="h-4 w-4" />
            <span>Unscheduled</span>
          </div>
          <h1 className="mt-2 text-3xl font-semibold tracking-normal">Unscheduled activities</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Review imported assignments and open activities before Solve is allowed to place them.
          </p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          <Button
            variant="outline"
            onClick={() =>
              inferPlanningForTasks(
                visibleUnscheduled.filter(shouldInferActivityPlanning).slice(0, 30).map(task => task.id)
              )
            }
            disabled={inferring || visibleUnscheduled.filter(shouldInferActivityPlanning).length === 0}
            className="w-full sm:w-auto"
          >
            <WandSparkles className={cn('mr-2 h-4 w-4', inferring && 'animate-pulse')} />
            Infer visible
          </Button>
          <Button variant="outline" onClick={refresh} disabled={refreshing} className="w-full sm:w-auto">
            <RefreshCw className={cn('mr-2 h-4 w-4', refreshing && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </header>

      {inferenceStatus && (
        <div className="rounded-md border border-cyan-200 bg-cyan-50 px-3 py-2 text-sm text-cyan-950">
          {inferenceStatus}
        </div>
      )}

      <section className="grid gap-3 sm:grid-cols-3">
        <Metric label="Open" value={String(unscheduled.length)} />
        <Metric label="Needs details" value={String(needsDetails.length)} tone="amber" />
        <Metric label="Ready" value={String(unscheduled.length - needsDetails.length)} tone="green" />
      </section>

      <section className="overflow-hidden rounded-md border bg-card">
        {unscheduled.length === 0 ? (
          <div className="flex min-h-52 flex-col items-center justify-center px-4 py-12 text-center">
            <CheckCircle2 className="h-9 w-9 text-emerald-500" />
            <h2 className="mt-3 text-lg font-medium">All caught up</h2>
            <p className="mt-1 max-w-md text-sm text-muted-foreground">
              New Canvas assignments and open activities will appear here before they enter the schedule.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1500px] text-sm">
              <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="sticky left-0 z-10 w-[280px] bg-muted/40 p-2">Activity</th>
                  <th className="w-[190px] p-2">Bundle</th>
                  <th className="w-[175px] p-2">Due</th>
                  <th className="w-[90px] p-2">Minutes</th>
                  <th className="w-[120px] p-2">Mode</th>
                  <th className="w-[90px] p-2">Req P</th>
                  <th className="w-[90px] p-2">Req RJ</th>
                  <th className="w-[90px] p-2">P return</th>
                  <th className="w-[90px] p-2">RJ return</th>
                  <th className="w-[90px] p-2">Resistance</th>
                  {availableAttributes.map(attr => (
                    <th key={attr.attr_id} className="w-[150px] p-2">
                      {attr.name}
                    </th>
                  ))}
                  <th className="w-[180px] p-2">Status</th>
                  <th className="sticky right-0 z-10 w-[120px] bg-muted/40 p-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleUnscheduled.map(task => {
                  const missing = getMissingPlanningFields(task, availableAttributes);
                  const missingLabel = formatMissingPlanningFields(missing);
                  const bundle = getBundle(task.bundleId) || bundles.find(item => item.id === task.bundleId);
                  const source = getSourceLabel(task);
                  const rowSaveStatus = autoSaveStatus[task.id];
                  return (
                    <tr key={task.id} className="border-t align-top">
                      <td className="sticky left-0 z-10 bg-card p-2">
                        <Input
                          value={task.title}
                          onChange={event => updateInlineTask(task.id, { title: event.target.value })}
                          className="h-8 min-w-0 font-medium"
                        />
                        <div className="mt-1 flex flex-wrap items-center gap-1">
                          {source && <Badge variant="secondary">{source}</Badge>}
                          {getPlanningInference(task) && (
                            <Badge className="bg-cyan-100 text-cyan-800 hover:bg-cyan-100">
                              Inferred
                            </Badge>
                          )}
                          {task.customAttributes?.local_title_override && (
                            <Badge className="bg-cyan-100 text-cyan-800 hover:bg-cyan-100">Local name</Badge>
                          )}
                        </div>
                        {getSourceTitle(task) && task.customAttributes?.local_title_override && (
                          <p className="mt-1 truncate text-xs text-muted-foreground">
                            Source: {getSourceTitle(task)}
                          </p>
                        )}
                      </td>
                      <td className="p-2">
                        <Select
                          value={task.bundleId || ''}
                          onValueChange={bundleId => {
                            const nextBundle = activeBundles.find(item => item.id === bundleId);
                            updateInlineTask(task.id, {
                              bundleId,
                              projectId: nextBundle?.projectId || null,
                            });
                          }}
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue placeholder="Bundle" />
                          </SelectTrigger>
                          <SelectContent>
                            {activeBundles.map(item => (
                              <SelectItem key={item.id} value={item.id}>
                                {item.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {bundle && <p className="mt-1 truncate text-xs text-muted-foreground">{bundle.name}</p>}
                      </td>
                      <td className="p-2">
                        <Input
                          type="datetime-local"
                          value={dateTimeInputValue(task.deadline)}
                          onChange={event =>
                            updateInlineTask(task.id, {
                              deadline: event.target.value
                                ? new Date(event.target.value).toISOString()
                                : null,
                            })
                          }
                          className="h-8"
                        />
                      </td>
                      <td className="p-2">
                        <NumberCell
                          value={task.estimatedDuration}
                          min={0}
                          step={5}
                          onChange={value =>
                            updateInlineTask(task.id, { estimatedDuration: value }, { saveDelayMs: 150 })
                          }
                        />
                      </td>
                      <td className="p-2">
                        <Badge variant="secondary">
                          {formatMode(deriveTaskStateMode(task.stateProfile))}
                        </Badge>
                      </td>
                      <ScoreCell task={task} field="required_productivity_energy" onChange={updateInlineProfile} />
                      <ScoreCell task={task} field="required_rejuvenation_energy" onChange={updateInlineProfile} />
                      <ScoreCell task={task} field="productivity_return" onChange={updateInlineProfile} />
                      <ScoreCell task={task} field="rejuvenation_return" onChange={updateInlineProfile} />
                      <ScoreCell task={task} field="resistance" onChange={updateInlineProfile} />
                      {availableAttributes.map(attr => (
                        <td key={attr.attr_id} className="p-2">
                          <AttributeCell
                            task={task}
                            attr={attr}
                            onChange={value => updateInlineAttribute(task, attr, value)}
                            onAttributeUpdated={handleAttributeDefinitionUpdated}
                          />
                        </td>
                      ))}
                      <td className="p-2">
                        {missing.length > 0 ? (
                          <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300">
                            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            <span className="line-clamp-3">
                              Missing {missingLabel}
                            </span>
                          </div>
                        ) : (
                          <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                            Ready
                          </Badge>
                        )}
                        <div
                          className={cn(
                            'mt-1 text-[11px]',
                            rowSaveStatus === 'error'
                              ? 'text-red-600'
                              : rowSaveStatus === 'saving'
                                ? 'text-muted-foreground'
                                : 'text-emerald-600'
                          )}
                        >
                          {rowSaveStatus === 'saving'
                            ? 'Autosaving...'
                            : rowSaveStatus === 'error'
                              ? 'Autosave failed'
                              : rowSaveStatus === 'saved'
                                ? 'Saved'
                                : getInferenceSummary(task) || 'Autosaves'}
                        </div>
                      </td>
                      <td className="sticky right-0 z-10 bg-card p-2">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => setEditingTask(task)} aria-label={`Edit ${task.title}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteTask(task)}
                            disabled={deletingId === task.id}
                            aria-label={`Delete ${task.title}`}
                            className="text-red-600 hover:bg-red-50 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {visibleUnscheduled.length < unscheduled.length && (
              <div className="flex justify-center border-t bg-card p-3">
                <Button
                  variant="outline"
                  onClick={() => setVisibleLimit(value => Math.min(value + 80, unscheduled.length))}
                >
                  Show {Math.min(80, unscheduled.length - visibleUnscheduled.length)} more activities
                </Button>
              </div>
            )}
          </div>
        )}
      </section>

      <TaskEditor
        task={editingTask}
        open={Boolean(editingTask)}
        onClose={() => setEditingTask(null)}
        onSave={handleSaveTask}
        onDelete={editingTask ? () => handleDeleteTask(editingTask) : undefined}
      />
    </div>
  );
}

function isUnscheduledActivity(task: TaskUI): boolean {
  return !hasUsableDate(task.scheduledStart) && !hasUsableDate(task.scheduledEnd);
}

function isVisibleUnscheduledActivity(task: TaskUI): boolean {
  return (
    isUnscheduledActivity(task) &&
    task.status !== 'completed' &&
    task.status !== 'done' &&
    task.status !== 'cancelled' &&
    !task.customAttributes?.duplicate_of
  );
}

function shouldInferActivityPlanning(task: TaskUI): boolean {
  const profile = task.stateProfile || {};
  const missingNativeEnergy =
    profile.required_productivity_energy == null ||
    profile.required_rejuvenation_energy == null ||
    profile.productivity_return == null ||
    profile.rejuvenation_return == null;
  const missingResistance = profile.resistance == null;
  const durationNeedsRefresh =
    task.customAttributes?.duration_predicted === true &&
    !getPlanningInference(task)?.duration_minutes;
  return missingNativeEnergy || missingResistance || durationNeedsRefresh;
}

function getPlanningInference(task: TaskUI): Record<string, any> | null {
  const inference = task.customAttributes?.planning_inference;
  return inference && typeof inference === 'object' && !Array.isArray(inference)
    ? inference
    : null;
}

function getInferenceSummary(task: TaskUI): string | null {
  const inference = getPlanningInference(task);
  if (!inference) return null;
  const confidence = Number(inference.confidence);
  const confidenceLabel = Number.isFinite(confidence) ? `${Math.round(confidence * 100)}%` : '';
  return confidenceLabel ? `Inferred ${confidenceLabel}` : 'Inferred';
}

function hasUsableDate(value?: string | null): boolean {
  if (!value) return false;
  const date = new Date(value);
  return Number.isFinite(date.getTime());
}

function dedupeImportedActivities(tasks: TaskUI[]): TaskUI[] {
  const bestByIdentity = new Map<string, TaskUI>();
  const output: TaskUI[] = [];
  for (const task of tasks) {
    const identity = importedActivityIdentity(task);
    if (!identity) {
      output.push(task);
      continue;
    }
    const current = bestByIdentity.get(identity);
    if (!current || scoreImportedActivity(task) > scoreImportedActivity(current)) {
      bestByIdentity.set(identity, task);
    }
  }
  const keptImportedIds = new Set([...bestByIdentity.values()].map(task => task.id));
  for (const task of tasks) {
    const identity = importedActivityIdentity(task);
    if (identity) {
      if (keptImportedIds.has(task.id)) output.push(task);
      continue;
    }
  }
  return output;
}

function importedActivityIdentity(task: TaskUI): string | null {
  if (String(task.customAttributes?.source || '').toLowerCase() !== 'canvas') return null;
  const canvas =
    task.customAttributes?.canvas && typeof task.customAttributes.canvas === 'object'
      ? task.customAttributes.canvas
      : {};
  if (typeof canvas.event_id === 'string' && canvas.event_id.trim()) return `canvas:event:${canvas.event_id}`;
  if (
    typeof canvas.google_event_id === 'string' &&
    canvas.google_event_id.trim() &&
    typeof canvas.calendar_id === 'string' &&
    canvas.calendar_id.trim()
  ) {
    return `canvas:gcal:${canvas.calendar_id}:${canvas.google_event_id}`;
  }
  const sourceTitle =
    typeof canvas.source_title === 'string' && canvas.source_title.trim()
      ? canvas.source_title
      : typeof task.customAttributes?.external_source_title === 'string'
        ? task.customAttributes.external_source_title
        : task.title;
  const dueAt = typeof canvas.due_at === 'string' && canvas.due_at.trim() ? canvas.due_at : task.deadline || '';
  return `canvas:title:${String(sourceTitle).toLowerCase().replace(/\s+/g, ' ').trim()}:${dueAt}`;
}

function scoreImportedActivity(task: TaskUI): number {
  const profile = task.stateProfile || {};
  const filledStateFields = [
    profile.required_productivity_energy,
    profile.required_rejuvenation_energy,
    profile.productivity_return,
    profile.rejuvenation_return,
    profile.resistance,
  ].filter(value => value !== null && value !== undefined && Number.isFinite(Number(value))).length;
  const updatedAt = Date.parse(task.updatedAt || task.createdAt || '');
  return (
    (task.customAttributes?.required_fields_complete === true ? 1000 : 0) +
    (task.customAttributes?.needs_review === false ? 250 : 0) +
    (hasUsableDate(task.scheduledStart) && hasUsableDate(task.scheduledEnd) ? 500 : 0) +
    filledStateFields * 25 +
    (task.bundleId ? 20 : 0) +
    (task.estimatedDuration ? 10 : 0) +
    Math.min(10, Number(task.completionPercent || 0)) +
    (Number.isFinite(updatedAt) ? Math.min(20, updatedAt / 1e12) : 0)
  );
}

function Metric({ label, value, tone = 'default' }: { label: string; value: string; tone?: 'default' | 'amber' | 'green' }) {
  const toneClass =
    tone === 'amber'
      ? 'border-amber-200 bg-amber-50 text-amber-950'
      : tone === 'green'
        ? 'border-emerald-200 bg-emerald-50 text-emerald-950'
        : 'border-border bg-card text-foreground';
  return (
    <div className={cn('rounded-md border p-3', toneClass)}>
      <div className="text-xs uppercase text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
    </div>
  );
}

function mergeTaskDraft<T extends Partial<TaskUI>>(base: T, draft?: Partial<TaskUI>): T {
  if (!draft) return base;
  return {
    ...base,
    ...draft,
    stateProfile:
      base.stateProfile || draft.stateProfile
        ? {
            ...(base.stateProfile || {}),
            ...(draft.stateProfile || {}),
          }
        : undefined,
    customAttributes:
      base.customAttributes || draft.customAttributes
        ? {
            ...(base.customAttributes || {}),
            ...(draft.customAttributes || {}),
          }
        : undefined,
  };
}

function NumberCell({
  value,
  min = 0,
  max,
  step = 0.5,
  onChange,
}: {
  value?: number | null;
  min?: number;
  max?: number;
  step?: number;
  onChange: (value: number | null) => void;
}) {
  return (
    <Input
      type="number"
      min={min}
      max={max}
      step={step}
      value={value ?? ''}
      onChange={event =>
        onChange(event.target.value === '' ? null : Number(event.target.value))
      }
      className="h-8"
    />
  );
}

function ScoreCell({
  task,
  field,
  onChange,
}: {
  task: TaskUI;
  field: StateScoreKey;
  onChange: (task: TaskUI, key: StateScoreKey, rawValue: string) => void;
}) {
  const value = task.stateProfile?.[field];
  return (
    <td className="p-2">
      <NumberCell
        value={typeof value === 'number' ? value : null}
        min={0}
        max={10}
        step={0.5}
        onChange={next => onChange(task, field, next == null ? '' : String(next))}
      />
    </td>
  );
}

type StateScoreKey = Exclude<keyof TaskStateProfile, 'mode'>;

function AttributeCell({
  task,
  attr,
  onChange,
  onAttributeUpdated,
}: {
  task: TaskUI;
  attr: AttributeDef;
  onChange: (value: string) => void;
  onAttributeUpdated: (attr: AttributeDef) => void;
}) {
  const value = getPlanningAttributeValue(task.customAttributes, attr) ?? '';
  if (attr.data_type === 'enum') {
    return (
      <EnumAttributeInput
        attr={attr}
        value={value}
        onChange={onChange}
        onAttributeUpdated={onAttributeUpdated}
        compact
      />
    );
  }

  return (
    <Input
      type={['Number', 'float', 'integer'].includes(attr.data_type) ? 'number' : 'text'}
      value={String(value)}
      onChange={event => onChange(event.target.value)}
      className="h-8"
    />
  );
}

function dateTimeInputValue(value?: string | null): string {
  if (!value) return '';
  const localEndOfDay = value.match(/^(\d{4})-(\d{2})-(\d{2})T23:59:00$/);
  if (localEndOfDay) return `${localEndOfDay[1]}-${localEndOfDay[2]}-${localEndOfDay[3]}T23:59`;
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

function getSourceLabel(task: TaskUI): string | null {
  const source = String(task.customAttributes?.source || '').toLowerCase();
  if (source === 'canvas') return 'Canvas';
  if (source === 'google-calendar' || source === 'gcal') return 'Google Calendar';
  if (source === 'book-tracker') return 'Book-Tracker';
  return null;
}

function getSourceTitle(task: TaskUI): string | null {
  const canvas = task.customAttributes?.canvas;
  if (canvas && typeof canvas === 'object' && typeof canvas.source_title === 'string') {
    return canvas.source_title;
  }
  return typeof task.customAttributes?.external_source_title === 'string'
    ? task.customAttributes.external_source_title
    : null;
}

function formatMode(mode: NonNullable<TaskStateProfile['mode']>): string {
  switch (mode) {
    case 'productive':
      return 'Productive';
    case 'rejuvenating':
      return 'Rejuvenating';
    case 'both':
      return 'Both';
    default:
      return 'Neutral';
  }
}
